import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';
import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import PatientsFilter from 'components/patient/PatientsFilter';
import PatientsTable from 'components/patient/PatientsTable';
import PatientAssignToolbar from 'components/workflow/PatientAssignToolbar';

import { fetchPatients } from 'redux/modules/patients';
import { addRouterParamsPageSettings } from 'redux/modules/routerParams';

import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';
import * as validate from 'utils/validate';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import dateFormat from 'config/dateFormat';

class OrderCreatePatientAssign extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onChangePage = this.onChangePage.bind(this);
    this.onChangeRowsPerPage = this.onChangeRowsPerPage.bind(this);
    this.onOrderPatientAssign = this.onOrderPatientAssign.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    
    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, limit, offset, actions, location, width } = this.props;
    const filtersChanged =
      nextProps.filters['Name or PRN'] !== filters['Name or PRN']
      || nextProps.filters['Date of Birth'] !== filters['Date of Birth']
      || nextProps.filters['Insurance Code'] !== filters['Insurance Code']
      || nextProps.filters['Patient Status'] !== filters['Patient Status'];
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (nextProps.count > 0 && nextProps.count <= offset) {
      newOffset = offset - limit;
    }

    if (newOffset !== offset) {
      actions.addRouterParamsPageSettings({
        [location.pathname]: {
          offset: newOffset,
        },
      });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps) {
    const { filters, limit, offset } = this.props;
    const limitChanged = prevProps.limit !== limit;
    const offsetChanged = prevProps.offset !== offset;
    const throttledFiltersChanged =
      (filters['Name or PRN']
      && filters['Name or PRN'].length > 0
      && prevProps.filters['Name or PRN'] !== filters['Name or PRN'])
      || (filters['Insurance Code']
      && filters['Insurance Code'].length > 0
      && prevProps.filters['Insurance Code'] !== filters['Insurance Code']);
    const filtersChanged = (!filters['Name or PRN']
      && prevProps.filters['Name or PRN']
      && prevProps.filters['Name or PRN'].length > 0
      && prevProps.filters['Name or PRN'] !== filters['Name or PRN'])
      || (!filters['Insurance Code']
        && prevProps.filters['Insurance Code']
        && prevProps.filters['Insurance Code'].length > 0
        && prevProps.filters['Insurance Code'] !== filters['Insurance Code'])
      || prevProps.filters['Date of Birth'] !== filters['Date of Birth']
      || prevProps.filters['Patient Status'] !== filters['Patient Status'];

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    }
  }

  onRequest() {
    const { actions, filters, limit, offset } = this.props;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map((key) => {
        let name = key;
        let operation = 'startswith';
        let caseSensitivity = null;
        let val = filters[key];
        switch (key) {
          case 'Name or PRN':
            if (/^\d+$/.test(filters[key])) {
              name = 'Record Number';
              operation = 'eq';
            } else {
              name = 'Full Name';
              caseSensitivity = false;
            }
            break;
          case 'Date of Birth':
            operation = 'eq';
            break;
          case 'Patient Status':
            operation = 'eq';
            val = filters[key] === 'Inactive' ? false : true;
            break;
          case 'Insurance Code':
            operation = 'startswith';
            caseSensitivity = false;
            break;
          default:
            caseSensitivity = false;
        }
        return {
          name,
          operation,
          val,
          ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
        };
      })
      : null;
    actions.fetchPatients({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
    });
  }

  onChangePage(event, page) {
    const { actions, location } = this.props;
    actions.addRouterParamsPageSettings({
      [location.pathname]: {
        offset: page * this.props.limit,
      },
    });
  }

  onChangeRowsPerPage(event) {
    const { actions, location } = this.props;
    window.scrollTo(0, 0);
    actions.addRouterParamsPageSettings({
      [location.pathname]: {
        limit: event.target.value,
        offset: 0,
      },
    });
  }

  onOrderPatientAssign(patient) {
    const { history } = this.props;
    history.push(`/workflow/order/create/patient/${patient['Record Number']}`);
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  render() {
    const { patients, count, loading, limit, offset } = this.props;
    const { mobileFilterExpanded } = this.state;
    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Create Order {titleSeparator} Assign Patient
          </title>
        </Helmet>
        <PatientAssignToolbar />
        <PatientsFilter
          form="chartsFilter"
          buttonHref="/workflow/order/create/patient-create"
          buttonVariant="add"
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <PatientsTable
          items={patients}
          page={offset / limit}
          rowsPerPage={limit}
          count={count}
          onChangePage={this.onChangePage}
          onChangeRowsPerPage={this.onChangeRowsPerPage}
          onRowClick={this.onOrderPatientAssign}
          loading={loading}
        />
      </Layout>
    );
  }
}

OrderCreatePatientAssign.propTypes = {
  patients: PropTypes.arrayOf(PropTypes.shape({
    'Record Number': PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Name or PRN': PropTypes.string,
    'Date of Birth': PropTypes.string,
    'Insurance Code': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchPatients: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  limit: PropTypes.number,
  offset: PropTypes.number,
};

OrderCreatePatientAssign.defaultProps = {
  limit: 10,
  offset: 0,
};

const mapStateToProps = (store, ownProps) => {
  const chartsFilterValues = getFormValues('chartsFilter')(store);
  return {
    patients: store.patients.data,
    count: store.patients.count,
    filters: {
      ...chartsFilterValues
        && chartsFilterValues['Name or PRN']
        ? { 'Name or PRN': chartsFilterValues['Name or PRN'] }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Date of Birth']
        && !validate.date(chartsFilterValues['Date of Birth'])
        ? { 'Date of Birth': moment(chartsFilterValues['Date of Birth'], dateFormat).format(dateFormat) }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Insurance Code']
        ? { 'Insurance Code': chartsFilterValues['Insurance Code'] }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Patient Status']
        && chartsFilterValues['Patient Status'] !== 'All'
        ? { 'Patient Status': chartsFilterValues['Patient Status'] }
        : {},
    },
    loading: store.patients.loading,
    ...store.routerParams.pageSettings[ownProps.location.pathname],
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatients,
    addRouterParamsPageSettings,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(OrderCreatePatientAssign),
);



// WEBPACK FOOTER //
// ./src/pages/workflow/OrderCreatePatientAssign.js